import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Plus, Trash2, MessageSquare, Loader2, Wrench, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Markdown, PersonAvatar } from "@/components/common";
import { ProposalCard } from "@/components/ProposalCard";
import { api, API, getToken, localNow, fmtDateTime, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const CEO_SUGGESTIONS = [
  "What needs my attention today?",
  "Review pending leave requests and recommend decisions",
  "Schedule a 30 min leadership sync tomorrow at 10am",
  "Which tasks are overdue? Suggest how to unblock them",
  "Plan onboarding for our newest hire",
  "Draft a team announcement about Friday's early finish",
];

const TOOL_LABELS = {
  get_company_snapshot: "Reading today's overview", list_employees: "Checking the staff directory",
  list_meetings: "Checking the calendar", list_tasks: "Reviewing tasks", list_leave_requests: "Reviewing leave",
  list_goals: "Reviewing goals", list_onboarding: "Checking onboarding",
};

function ConversationList({ convs, active, onSelect, onNew, onDelete }) {
  return (
    <div className="flex flex-col h-full">
      <Button onClick={onNew} className="w-full" data-testid="ai-new-conversation-button"><Plus className="h-4 w-4 mr-1.5" />New conversation</Button>
      <ScrollArea className="flex-1 mt-3 -mx-1">
        <div className="space-y-1 px-1">
          {convs.length === 0 && <p className="text-xs text-muted-foreground px-2 py-3">No conversations yet.</p>}
          {convs.map((c) => (
            <div key={c.conversation_id}
              className={`group flex items-center gap-2 rounded-lg px-2.5 py-2 cursor-pointer text-sm transition-colors ${active === c.conversation_id ? "bg-muted font-medium" : "hover:bg-muted/70"}`}
              onClick={() => onSelect(c.conversation_id)} data-testid="ai-conversation-item">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate">{c.title}</p>
                <p className="text-[11px] text-muted-foreground">{fmtDateTime(c.updated_at)}</p>
              </div>
              <button className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-card" aria-label="Delete conversation"
                onClick={(e) => { e.stopPropagation(); onDelete(c.conversation_id); }} data-testid="ai-delete-conversation-button">
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function AdvisorChat({ role = "ceo", persona, className = "h-[calc(100vh-7.5rem)]" }) {
  const SUGGESTIONS = persona.suggestions;
  const PIcon = persona.icon || Sparkles;
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [cid, setCid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const endRef = useRef(null);
  const autoSent = useRef(false);

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get("/ai/conversations", { params: { role } }); setConvs(data); } catch { /* ignore */ }
  }, [role]);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { setCid(null); setMessages([]); }, [role]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, toolStatus]);

  const openConv = async (id) => {
    setHistoryOpen(false);
    if (streaming) return;
    try {
      const { data } = await api.get(`/ai/conversations/${id}`);
      setCid(id);
      setMessages(data.messages);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const newConv = () => { if (streaming) return; setCid(null); setMessages([]); setHistoryOpen(false); };

  const deleteConv = async (id) => {
    await api.delete(`/ai/conversations/${id}`);
    if (id === cid) newConv();
    loadConvs();
  };

  const updateProposal = (p) => {
    setMessages((ms) => ms.map((m) => ({ ...m, proposals: (m.proposals || []).map((x) => (x.action_id === p.action_id ? p : x)) })));
  };

  const send = useCallback(async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setToolStatus("Thinking");
    setMessages((ms) => [...ms, { role: "user", content: text, message_id: `u${Date.now()}` }, { role: "assistant", content: "", proposals: [], message_id: `a${Date.now()}`, _live: true }]);
    const patchLive = (fn) => setMessages((ms) => ms.map((m) => (m._live ? fn(m) : m)));
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken() || ""}` },
        body: JSON.stringify({ message: text, conversation_id: cid, now: localNow(), role }),
      });
      if (!res.ok || !res.body) {
        let detail = "The AI CEO couldn't respond.";
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5)); } catch { continue; }
          if (ev.type === "meta") setCid(ev.conversation_id);
          else if (ev.type === "delta") { setToolStatus(""); patchLive((m) => ({ ...m, content: m.content + ev.content })); }
          else if (ev.type === "tool") setToolStatus(TOOL_LABELS[ev.name] || "Preparing a proposal");
          else if (ev.type === "proposal") patchLive((m) => ({ ...m, proposals: [...m.proposals, ev.proposal] }));
          else if (ev.type === "error") toast.error(ev.message);
        }
      }
    } catch (e) {
      toast.error(e.message || "Connection lost");
      patchLive((m) => ({ ...m, content: m.content || "_Sorry, I couldn't respond. Please try again._" }));
    } finally {
      setMessages((ms) => ms.map((m) => (m._live ? { ...m, _live: false } : m)));
      setStreaming(false);
      setToolStatus("");
      loadConvs();
    }
  }, [input, streaming, cid, loadConvs, role]);

  useEffect(() => {
    const q = params.get("q");
    if (q && !autoSent.current) {
      autoSent.current = true;
      setParams({}, { replace: true });
      if (q.trim().endsWith("about")) setInput(q + " ");
      else send(q);
    }
  }, [params, setParams, send]);

  const sidebar = <ConversationList convs={convs} active={cid} onSelect={openConv} onNew={newConv} onDelete={deleteConv} />;

  return (
    <div className={`flex gap-6 ${className}`} data-testid={`advisor-chat-${role}`}>
      <aside className="hidden xl:flex w-[260px] shrink-0 flex-col bg-card border border-border rounded-xl p-3">{sidebar}</aside>
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="left" className="w-[290px] bg-card pt-10"><SheetTitle className="sr-only">Conversations</SheetTitle>{sidebar}</SheetContent>
      </Sheet>

      <section className="flex-1 min-w-0 flex flex-col bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-border">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><PIcon className="h-4 w-4 text-white" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{persona.name}</p>
            <p className="text-xs text-muted-foreground truncate">{persona.tagline}</p>
          </div>
          <Button variant="ghost" size="sm" className="xl:hidden" onClick={() => setHistoryOpen(true)} data-testid="ai-history-button"><History className="h-4 w-4 mr-1" />History</Button>
          <Button variant="ghost" size="sm" className="xl:hidden" onClick={newConv} aria-label="New conversation" data-testid="ai-new-conversation-mobile"><Plus className="h-4 w-4" /></Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-4 sm:px-6 py-6 space-y-6 max-w-3xl mx-auto" data-testid="ai-chat-stream">
            {messages.length === 0 && (
              <div className="py-8">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{persona.greeting}</h2>
                <p className="text-sm text-muted-foreground mt-2">{persona.intro}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-6">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} onClick={() => send(s)} className="text-left text-sm bg-background border border-border rounded-xl px-4 py-3 hover:bg-muted transition-colors" data-testid={`ai-suggestion-${i}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.message_id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-message-${m.role}`}>
                {m.role === "assistant" && <div className="h-7 w-7 shrink-0 rounded-lg bg-primary flex items-center justify-center mt-0.5"><PIcon className="h-3.5 w-3.5 text-white" /></div>}
                <div className={`min-w-0 ${m.role === "user" ? "max-w-[85%]" : "flex-1"}`}>
                  {m.role === "user" ? (
                    <div className="bg-accent text-accent-foreground rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap">{m.content}</div>
                  ) : (
                    <div className="space-y-3">
                      {m.content && <div className="bg-background border border-border rounded-2xl px-4 py-3"><Markdown>{m.content}</Markdown></div>}
                      {m._live && toolStatus && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ai-tool-status">
                          {toolStatus === "Thinking" ? <span className="flex gap-1"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></span> : <Wrench className="h-3.5 w-3.5" />}
                          {toolStatus}…
                        </div>
                      )}
                      {(m.proposals || []).map((p) => <ProposalCard key={p.action_id} proposal={p} onChange={updateProposal} />)}
                    </div>
                  )}
                </div>
                {m.role === "user" && <PersonAvatar name={user.name} src={user.picture} className="h-7 w-7 mt-0.5" />}
              </div>
            ))}
            <div ref={endRef} />
          </div>
        </ScrollArea>

        <div className="border-t border-border p-3 sm:p-4">
          <form className="max-w-3xl mx-auto flex items-end gap-2 bg-background border border-border rounded-2xl p-2 focus-within:ring-2 focus-within:ring-ring"
            onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={persona.placeholder}
              rows={1}
              className="min-h-[44px] max-h-40 resize-none border-0 shadow-none focus-visible:ring-0 bg-transparent"
              data-testid="ai-chat-composer-textarea"
            />
            <Button type="submit" size="icon" disabled={streaming || !input.trim()} aria-label="Send" data-testid="ai-chat-composer-send-button">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
